
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';

// Define the employer type
interface Employer {
  id: string;
  email: string;
  company_name: string;
  created_at: string;
  expires_at: string | null;
}

// Define form schema with Zod
const formSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(8, { message: 'Password must be at least 8 characters' }),
  company_name: z.string().min(2, { message: 'Company name is required' }),
  expires_at: z.string().optional(),
  active: z.boolean().default(true)
});

type FormValues = z.infer<typeof formSchema>;

const EmployerManagement = () => {
  const [employers, setEmployers] = useState<Employer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentEmployerId, setCurrentEmployerId] = useState<string | null>(null);

  // Initialize the form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      company_name: '',
      expires_at: '',
      active: true
    }
  });

  // Fetch employers on component mount
  useEffect(() => {
    fetchEmployers();
  }, []);

  const fetchEmployers = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('employers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        throw error;
      }

      setEmployers(data || []);
    } catch (error) {
      console.error('Error fetching employers:', error);
      toast.error('Failed to load employers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    form.reset({
      email: '',
      password: '',
      company_name: '',
      expires_at: '',
      active: true
    });
    setEditMode(false);
    setCurrentEmployerId(null);
  };

  const handleEditEmployer = (employer: Employer) => {
    setEditMode(true);
    setCurrentEmployerId(employer.id);
    
    // Convert UTC date to local date format for input
    let expiryDate = '';
    if (employer.expires_at) {
      const date = new Date(employer.expires_at);
      expiryDate = format(date, 'yyyy-MM-dd');
    }
    
    form.reset({
      email: employer.email,
      password: '', // Don't set password when editing
      company_name: employer.company_name,
      expires_at: expiryDate,
      active: true // This should be dynamically set based on expiry
    });
  };

  const handleDeleteEmployer = async (employerId: string) => {
    if (!confirm('Are you sure you want to delete this employer?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('employers')
        .delete()
        .eq('id', employerId);

      if (error) throw error;
      
      toast.success('Employer deleted successfully');
      fetchEmployers();
    } catch (error) {
      console.error('Error deleting employer:', error);
      toast.error('Failed to delete employer');
    }
  };

  const onSubmit = async (values: FormValues) => {
    try {
      let expiresAt = null;
      if (values.expires_at) {
        // Create a UTC date from the local date input
        const dateObj = new Date(values.expires_at);
        expiresAt = dateObj.toISOString();
      }

      if (editMode && currentEmployerId) {
        // Update existing employer
        const updateData: any = {
          company_name: values.company_name,
          expires_at: expiresAt
        };

        // Only include password if it was provided (not empty)
        if (values.password.trim() !== '') {
          // In a real application, you would hash the password here or use a 
          // secure authentication method to update credentials
          updateData.password = values.password;
        }

        const { error } = await supabase
          .from('employers')
          .update(updateData)
          .eq('id', currentEmployerId);

        if (error) throw error;
        toast.success('Employer updated successfully');
      } else {
        // Create new employer
        const { error } = await supabase
          .from('employers')
          .insert([
            {
              email: values.email,
              password: values.password, // In a real app, hash this password
              company_name: values.company_name,
              expires_at: expiresAt
            }
          ]);

        if (error) throw error;
        toast.success('Employer created successfully');
      }

      resetForm();
      fetchEmployers();
    } catch (error) {
      console.error('Error saving employer:', error);
      toast.error('Failed to save employer');
    }
  };

  const isExpired = (expiresAt: string | null): boolean => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{editMode ? 'Edit Employer' : 'Add New Employer'}</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        placeholder="employer@company.com" 
                        disabled={editMode} // Email can't be changed in edit mode
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{editMode ? 'New Password (leave blank to keep current)' : 'Password'}</FormLabel>
                    <FormControl>
                      <Input 
                        {...field} 
                        type="password" 
                        placeholder={editMode ? "••••••••" : "Enter password"} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="company_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Company Name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="expires_at"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiration Date (Optional)</FormLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="flex items-center justify-end space-x-2 pt-4">
                {editMode && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={resetForm}
                  >
                    Cancel
                  </Button>
                )}
                <Button type="submit">
                  {editMode ? 'Update Employer' : 'Add Employer'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Current Employers</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-4">Loading employers...</div>
          ) : employers.length === 0 ? (
            <div className="text-center py-4">No employers found.</div>
          ) : (
            <div className="space-y-4">
              {employers.map((employer) => (
                <div
                  key={employer.id}
                  className={`p-4 border rounded-md ${
                    isExpired(employer.expires_at) ? 'bg-red-50 border-red-200' : 'bg-white'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium">{employer.company_name}</h3>
                      <p className="text-sm text-gray-500">{employer.email}</p>
                      <div className="mt-1 text-xs">
                        {employer.expires_at ? (
                          <span className={isExpired(employer.expires_at) ? 'text-red-500' : 'text-green-500'}>
                            {isExpired(employer.expires_at)
                              ? `Expired: ${format(new Date(employer.expires_at), 'PPP')}`
                              : `Expires: ${format(new Date(employer.expires_at), 'PPP')}`}
                          </span>
                        ) : (
                          <span className="text-green-500">No expiration date</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditEmployer(employer)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteEmployer(employer.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployerManagement;
